import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import api from "api";

/**
 * Modal reutilizável: timeline de atividades + sessões de um usuário (admin).
 */
export default function UserTimelineModal({ show, onHide, userId, username, token }) {
  const [timelineData, setTimelineData] = useState(null);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);

  useEffect(() => {
    if (!show || !userId || !token) {
      setTimelineData(null);
      setTimelineError(null);
      return;
    }
    const fetchTimeline = async () => {
      setTimelineLoading(true);
      setTimelineError(null);
      try {
        const { data } = await api.get(`/users/${userId}/activity-timeline`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTimelineData(data);
      } catch {
        setTimelineError("Erro ao carregar timeline do usuário");
        setTimelineData(null);
      } finally {
        setTimelineLoading(false);
      }
    };
    fetchTimeline();
  }, [show, userId, token]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fas fa-user-clock me-2" />
          Timeline — {username || `Usuário #${userId}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {timelineLoading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        )}
        {timelineError && (
          <Alert variant="danger" className="mb-0">
            {timelineError}
          </Alert>
        )}
        {!timelineLoading && timelineData && (
          <>
            <p className="text-muted small mb-3">
              {timelineData.user?.email && (
                <span className="me-3">{timelineData.user.email}</span>
              )}
              {timelineData.activities?.length || 0} atividades ·{" "}
              {timelineData.sessions?.length || 0} sessões
            </p>
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {(() => {
                const items = [];
                (timelineData.activities || []).forEach(a => {
                  items.push({
                    date: a.timestamp ? new Date(a.timestamp) : null,
                    type: "activity",
                    ...a
                  });
                });
                (timelineData.sessions || []).forEach(s => {
                  items.push({
                    date: s.session_start ? new Date(s.session_start) : null,
                    type: "session",
                    ...s
                  });
                });
                items.sort((a, b) => (b.date && a.date ? b.date - a.date : 0));
                return (
                  <ul className="list-unstyled mb-0">
                    {items.map((item, idx) => (
                      <li
                        key={item.type + "-" + (item.id || idx)}
                        className="d-flex border-bottom py-2 align-items-start"
                        style={{ gap: "12px" }}
                      >
                        <span
                          className="badge mt-1"
                          style={{
                            minWidth: "70px",
                            backgroundColor:
                              item.type === "session" ? "#035184" : "#0d6a4a"
                          }}
                        >
                          {item.type === "session" ? "Sessão" : "Atividade"}
                        </span>
                        <div className="flex-grow-1">
                          <span className="text-muted small">
                            {item.date
                              ? item.date.toLocaleString("pt-BR", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "2-digit",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                  second: "2-digit"
                                })
                              : "—"}
                          </span>
                          {item.type === "session" ? (
                            <div className="small">
                              Sessão iniciada
                              {(item.ip_address || item.city || item.country) && (
                                <span className="text-muted">
                                  {" "}
                                  —{" "}
                                  {[item.city, item.country]
                                    .filter(Boolean)
                                    .join(", ") || item.ip_address}
                                </span>
                              )}
                              {item.browser && (
                                <span className="text-muted"> · {item.browser}</span>
                              )}
                              {item.session_duration != null && (
                                <span className="text-muted">
                                  {" "}
                                  · {Math.round(item.session_duration / 60)} min
                                </span>
                              )}
                            </div>
                          ) : (
                            <div>
                              {item.activity_description}
                              {(item.project_name || item.endpoint) && (
                                <span className="text-muted small">
                                  {" "}
                                  {item.project_name && `(${item.project_name})`}
                                  {item.endpoint && ` · ${item.endpoint}`}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                );
              })()}
              {timelineData.activities?.length === 0 &&
                timelineData.sessions?.length === 0 && (
                  <div className="text-center text-muted py-4">
                    Nenhuma atividade ou sessão registrada para este usuário.
                  </div>
                )}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
